import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Inbox, Send, RefreshCw, Search, PenSquare, AlertTriangle, FileText, MessageSquare,
  Mail, Loader2, Archive, Trash2, Paperclip, MailOpen, Save, FilePenLine,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  adminMailboxBoites, adminMailboxMessages, adminMailboxMessage, adminMailboxMark,
  adminMailboxArchive, adminMailboxTrash, adminDownloadAttachment,
  adminSendEmail, adminListDrafts, adminCreateDraft, adminUpdateDraft, adminDeleteDraft,
  adminListSent,
  adminGetQuoteRequests, adminGetContactMessages,
  adminUpdateQuoteRequest, adminUpdateContactMessage,
} from '../api/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-BE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const octets = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

const STATUTS_DEVIS = [
  { valeur: 'new', libelle: 'Nouveau' },
  { valeur: 'contacted', libelle: 'Contacté' },
  { valeur: 'completed', libelle: 'Terminé' },
];
const STATUTS_MESSAGE = [
  { valeur: 'new', libelle: 'Nouveau' },
  { valeur: 'read', libelle: 'Lu' },
  { valeur: 'replied', libelle: 'Répondu' },
];

// ------------------------------------------------------------------ Composer
const Composer = ({ ouvert, onClose, initial, boites, onEnvoye }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sujet, setSujet] = useState('');
  const [message, setMessage] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [sauvegarde, setSauvegarde] = useState(false);

  useEffect(() => {
    if (ouvert) {
      setFrom(initial?.from_mailbox || boites[0]?.key || '');
      setTo(initial?.to || '');
      setSujet(initial?.subject || '');
      setMessage(initial?.message || '');
      setDraftId(initial?.draftId || null);
    }
  }, [ouvert, initial, boites]);

  const envoyer = async (e) => {
    e.preventDefault();
    if (!to || !sujet.trim() || !message.trim()) {
      toast.error('Destinataire, objet et message sont requis.');
      return;
    }
    setEnvoi(true);
    try {
      await adminSendEmail({ to, subject: sujet, message, from_mailbox: from || undefined });
      if (draftId) { try { await adminDeleteDraft(draftId); } catch { /* brouillon déjà parti */ } }
      toast.success('Email envoyé.');
      onEnvoye?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'envoi a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  const enregistrerBrouillon = async () => {
    setSauvegarde(true);
    try {
      const corps = { to, subject: sujet, message, from_mailbox: from || undefined };
      const enr = draftId ? await adminUpdateDraft(draftId, corps) : await adminCreateDraft(corps);
      setDraftId(enr.id);
      toast.success('Brouillon enregistré.');
      onEnvoye?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setSauvegarde(false);
    }
  };

  const adresseLabel = boites.find((b) => b.key === from)?.label;

  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{draftId ? 'Brouillon' : 'Nouvel email'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={envoyer} className="space-y-4">
          {boites.length > 1 && (
            <div className="space-y-2">
              <Label htmlFor="from">Depuis</Label>
              <select
                id="from"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full h-11 rounded-md border border-gray-200 bg-white px-3 text-sm"
              >
                {boites.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="to">Destinataire</Label>
            <Input id="to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sujet">Objet</Label>
            <Input id="sujet" value={sujet} onChange={(e) => setSujet(e.target.value)}
              placeholder="Votre devis NETTO+" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="corps">Message</Label>
            <Textarea id="corps" rows={8} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Bonjour,&#10;&#10;..." required />
          </div>
          <p className="text-xs text-gray-500">
            Envoyé depuis {adresseLabel || 'contact@nettoplus.be'}. Les réponses reviennent sur cette même adresse.
          </p>
          <div className="flex justify-between gap-3">
            <Button type="button" variant="outline" className="gap-2" onClick={enregistrerBrouillon} disabled={sauvegarde}>
              {sauvegarde ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Brouillon
            </Button>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
              <Button type="submit" disabled={envoi} className="gap-2">
                {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {envoi ? 'Envoi…' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ------------------------------------------------------ Onglet boîte de réception
const OngletBoite = ({ boites, onRepondre }) => {
  const [boiteActive, setBoiteActive] = useState('toutes');
  const [messages, setMessages] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(null);
  const [chargementMsg, setChargementMsg] = useState(false);
  const [action, setAction] = useState(false);

  const multi = boites.length > 1;

  const charger = useCallback(async (rechercheActuelle = '', boite = boiteActive) => {
    setChargement(true);
    try {
      const params = { limit: 50 };
      if (boite && boite !== 'toutes') params.boite = boite;
      if (rechercheActuelle) params.search = rechercheActuelle;
      const data = await adminMailboxMessages(params);
      setMessages(data.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Boîte injoignable.');
      setMessages([]);
    } finally {
      setChargement(false);
    }
  }, [boiteActive]);

  useEffect(() => { charger(recherche, boiteActive); /* eslint-disable-next-line */ }, [boiteActive]);

  const labelBoite = (key) => boites.find((b) => b.key === key)?.label || key;

  const ouvrir = async (m) => {
    setChargementMsg(true);
    setOuvert({ uid: m.uid, mailbox: m.mailbox, subject: '…' });
    try {
      const msg = await adminMailboxMessage(m.uid, m.mailbox);
      setOuvert(msg);
      setMessages((liste) => liste.map((x) => (x.uid === m.uid && x.mailbox === m.mailbox ? { ...x, seen: true } : x)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Lecture impossible.');
      setOuvert(null);
    } finally {
      setChargementMsg(false);
    }
  };

  const retirerDeLaListe = (uid, mailbox) =>
    setMessages((liste) => liste.filter((x) => !(x.uid === uid && x.mailbox === mailbox)));

  const marquerNonLu = async (m) => {
    try {
      await adminMailboxMark(m.uid, m.mailbox, false);
      setMessages((liste) => liste.map((x) => (x.uid === m.uid && x.mailbox === m.mailbox ? { ...x, seen: false } : x)));
      setOuvert(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Action impossible.'); }
  };

  const archiver = async (m, fermer) => {
    setAction(true);
    try {
      await adminMailboxArchive(m.uid, m.mailbox);
      retirerDeLaListe(m.uid, m.mailbox);
      if (fermer) setOuvert(null);
      toast.success('Email archivé.');
    } catch (error) { toast.error(error.response?.data?.detail || 'Archivage impossible.'); }
    finally { setAction(false); }
  };

  const supprimer = async (m, fermer) => {
    setAction(true);
    try {
      await adminMailboxTrash(m.uid, m.mailbox);
      retirerDeLaListe(m.uid, m.mailbox);
      if (fermer) setOuvert(null);
      toast.success('Email déplacé vers la corbeille.');
    } catch (error) { toast.error(error.response?.data?.detail || 'Suppression impossible.'); }
    finally { setAction(false); }
  };

  const telecharger = async (piece) => {
    try {
      await adminDownloadAttachment(ouvert.uid, ouvert.mailbox, piece.index, piece.filename);
    } catch (error) { toast.error('Téléchargement impossible.'); }
  };

  if (!boites.length) {
    return (
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold">Aucune boîte accessible</p>
          <p className="mt-1">
            Aucune boîte n'est configurée ou votre rôle n'a pas accès à la messagerie.
            Configurez MAILBOX_USER/MAILBOX_PASSWORD (ou MAILBOXES) dans <code>backend/.env</code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtre par adresse */}
      {multi && (
        <div className="flex flex-wrap gap-2">
          <FiltreBoite actif={boiteActive === 'toutes'} onClick={() => setBoiteActive('toutes')}>
            Toutes
          </FiltreBoite>
          {boites.map((b) => (
            <FiltreBoite key={b.key} actif={boiteActive === b.key} onClick={() => setBoiteActive(b.key)} pastille={b.non_lus}>
              {b.label}
            </FiltreBoite>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <form className="relative flex-1 min-w-[220px]"
          onSubmit={(e) => { e.preventDefault(); charger(recherche, boiteActive); }}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
          <Input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher (objet, expéditeur)…" className="pl-9 h-11" />
        </form>
        <Button variant="outline" className="gap-2 h-11" onClick={() => charger(recherche, boiteActive)}>
          <RefreshCw className={`w-4 h-4 ${chargement ? 'animate-spin' : ''}`} aria-hidden="true" />
          Actualiser
        </Button>
      </div>

      {chargement ? (
        <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" /></div>
      ) : messages.length === 0 ? (
        <p className="text-center text-gray-500 py-12">Aucun email.</p>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
          {messages.map((m) => (
            <div key={`${m.mailbox}-${m.uid}`}
              className={`group flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${m.seen ? '' : 'bg-blue-50/40'}`}>
              <button onClick={() => ouvrir(m)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                <span className={`w-2 h-2 rounded-full shrink-0 ${m.seen ? 'bg-transparent' : 'bg-blue-600'}`} aria-hidden="true" />
                {multi && boiteActive === 'toutes' && (
                  <Badge className="shrink-0 bg-gray-100 text-gray-600 hover:bg-gray-100 font-normal">{labelBoite(m.mailbox)}</Badge>
                )}
                <span className={`w-44 shrink-0 truncate text-sm ${m.seen ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                  {m.from_name || m.from_email}
                </span>
                <span className={`flex-1 truncate text-sm ${m.seen ? 'text-gray-600' : 'font-medium text-gray-900'}`}>{m.subject}</span>
              </button>
              <span className="text-xs text-gray-400 shrink-0 hidden sm:block">{formatDate(m.date)}</span>
              <div className="flex items-center gap-1 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                <button title="Archiver" onClick={() => archiver(m, false)} disabled={action}
                  className="p-1.5 rounded hover:bg-gray-200 text-gray-500"><Archive className="w-4 h-4" /></button>
                <button title="Corbeille" onClick={() => supprimer(m, false)} disabled={action}
                  className="p-1.5 rounded hover:bg-red-100 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lecture d'un message */}
      <Dialog open={!!ouvert} onOpenChange={(o) => !o && setOuvert(null)}>
        <DialogContent className="max-w-2xl">
          {ouvert && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">{ouvert.subject}</DialogTitle>
              </DialogHeader>
              {chargementMsg ? (
                <div className="flex justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" /></div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 border-b border-gray-100 pb-3">
                    <div><span className="text-gray-400">De :</span> {ouvert.from_name} &lt;{ouvert.from_email}&gt;</div>
                    {multi && <div><span className="text-gray-400">Boîte :</span> {labelBoite(ouvert.mailbox)}</div>}
                    <div><span className="text-gray-400">Date :</span> {formatDate(ouvert.date)}</div>
                  </div>

                  {ouvert.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {ouvert.attachments.map((p) => (
                        <button key={p.index} onClick={() => telecharger(p)}
                          className="inline-flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 hover:border-blue-600 hover:text-blue-600 transition-colors">
                          <Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
                          <span className="truncate max-w-[200px]">{p.filename}</span>
                          {p.size ? <span className="text-gray-400 text-xs">{octets(p.size)}</span> : null}
                        </button>
                      ))}
                    </div>
                  )}

                  {ouvert.html ? (
                    <iframe title="Contenu de l'email" sandbox="" srcDoc={ouvert.html}
                      className="w-full h-[50vh] border border-gray-100 rounded" />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 max-h-[50vh] overflow-auto">
                      {ouvert.text || '(message vide)'}
                    </pre>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                      <button title="Marquer comme non lu" onClick={() => marquerNonLu(ouvert)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm text-gray-600 hover:bg-gray-100">
                        <MailOpen className="w-4 h-4" /> Non lu
                      </button>
                      <button title="Archiver" onClick={() => archiver(ouvert, true)} disabled={action}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm text-gray-600 hover:bg-gray-100">
                        <Archive className="w-4 h-4" /> Archiver
                      </button>
                      <button title="Corbeille" onClick={() => supprimer(ouvert, true)} disabled={action}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm text-gray-600 hover:bg-red-100 hover:text-red-600">
                        <Trash2 className="w-4 h-4" /> Supprimer
                      </button>
                    </div>
                    <Button className="gap-2"
                      onClick={() => { onRepondre({ to: ouvert.from_email, subject: `Re: ${ouvert.subject}`, from_mailbox: ouvert.mailbox }); setOuvert(null); }}>
                      <Send className="w-4 h-4" aria-hidden="true" /> Répondre
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FiltreBoite = ({ actif, onClick, children, pastille }) => (
  <button onClick={onClick}
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${actif ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-600'}`}>
    {children}
    {pastille > 0 && (
      <span className={`text-xs rounded-full px-1.5 ${actif ? 'bg-white/25' : 'bg-blue-100 text-blue-700'}`}>{pastille}</span>
    )}
  </button>
);

// ----------------------------------------------------------- Onglet brouillons
const OngletBrouillons = ({ onOuvrir, rafraichir }) => {
  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    try { setItems((await adminListDrafts()).items || []); }
    catch { toast.error('Chargement des brouillons impossible.'); }
    finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger, rafraichir]);

  const supprimer = async (id) => {
    try { await adminDeleteDraft(id); setItems((l) => l.filter((d) => d.id !== id)); }
    catch { toast.error('Suppression impossible.'); }
  };

  if (chargement) return <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!items.length) return <p className="text-center text-gray-500 py-12">Aucun brouillon.</p>;

  return (
    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
      {items.map((d) => (
        <div key={d.id} className="group flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
          <button onClick={() => onOuvrir(d)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
            <FilePenLine className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
            <span className="w-44 shrink-0 truncate text-sm text-gray-700">{d.to || '(sans destinataire)'}</span>
            <span className="flex-1 truncate text-sm text-gray-600">{d.subject || '(sans objet)'}</span>
          </button>
          <span className="text-xs text-gray-400 shrink-0 hidden sm:block">{formatDate(d.updated_at)}</span>
          <button title="Supprimer" onClick={() => supprimer(d.id)}
            className="p-1.5 rounded hover:bg-red-100 text-gray-500 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
};

// --------------------------------------------------- Onglet demandes du site
const OngletDemandes = ({ onRepondre }) => {
  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [ouvert, setOuvert] = useState(null);
  const [note, setNote] = useState('');
  const [statutSel, setStatutSel] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [devis, messages] = await Promise.all([
        adminGetQuoteRequests({ limit: 200 }),
        adminGetContactMessages({ limit: 200 }),
      ]);
      const fusion = [
        ...devis.items.map((d) => ({ ...d, _type: 'devis' })),
        ...messages.items.map((m) => ({ ...m, _type: 'message' })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setItems(fusion);
    } catch (error) {
      toast.error('Chargement des demandes impossible.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = (item) => { setOuvert(item); setNote(item.admin_note || ''); setStatutSel(item.status); };

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const corps = { status: statutSel, admin_note: note };
      const maj = ouvert._type === 'devis'
        ? await adminUpdateQuoteRequest(ouvert.id, corps)
        : await adminUpdateContactMessage(ouvert.id, corps);
      setItems((liste) => liste.map((it) => (it.id === ouvert.id && it._type === ouvert._type ? { ...maj, _type: ouvert._type } : it)));
      setOuvert(null);
      toast.success('Demande mise à jour.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enregistrement impossible.');
    } finally {
      setEnregistrement(false);
    }
  };

  const filtres = recherche
    ? items.filter((it) => [it.name, it.email, it.phone, it.service, it.message].filter(Boolean).join(' ').toLowerCase().includes(recherche.toLowerCase()))
    : items;

  const statutsPour = (type) => (type === 'devis' ? STATUTS_DEVIS : STATUTS_MESSAGE);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
        <Input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)}
          placeholder="Rechercher (nom, email, service…)" className="pl-9 h-11" />
      </div>

      {chargement ? (
        <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" /></div>
      ) : filtres.length === 0 ? (
        <p className="text-center text-gray-500 py-12">Aucune demande.</p>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
          {filtres.map((it) => (
            <button key={`${it._type}-${it.id}`} onClick={() => ouvrir(it)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3">
              <Badge className={`shrink-0 gap-1 ${it._type === 'devis' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' : 'bg-purple-100 text-purple-700 hover:bg-purple-100'}`}>
                {it._type === 'devis' ? <FileText className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                {it._type === 'devis' ? 'Devis' : 'Message'}
              </Badge>
              <span className="w-40 shrink-0 truncate text-sm font-medium text-gray-900">{it.name}</span>
              <span className="flex-1 truncate text-sm text-gray-600">{it._type === 'devis' ? it.service : it.message}</span>
              {it.status === 'new' && <span className="shrink-0 text-xs font-semibold text-blue-600">Nouveau</span>}
              <span className="text-xs text-gray-400 shrink-0 hidden sm:block">{formatDate(it.created_at)}</span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!ouvert} onOpenChange={(o) => !o && setOuvert(null)}>
        <DialogContent className="max-w-lg">
          {ouvert && (
            <>
              <DialogHeader>
                <DialogTitle>{ouvert._type === 'devis' ? 'Demande de devis' : 'Message de contact'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="text-sm space-y-1">
                  <div><span className="text-gray-400">Nom :</span> {ouvert.name}</div>
                  <div><span className="text-gray-400">Email :</span> {ouvert.email}</div>
                  {ouvert.phone && <div><span className="text-gray-400">Téléphone :</span> {ouvert.phone}</div>}
                  {ouvert._type === 'devis' && <div><span className="text-gray-400">Service :</span> {ouvert.service}</div>}
                  <div><span className="text-gray-400">Reçu le :</span> {formatDate(ouvert.created_at)}</div>
                </div>
                {ouvert.message && (
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap">{ouvert.message}</div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="statut">Statut</Label>
                  <select id="statut" value={statutSel} onChange={(e) => setStatutSel(e.target.value)}
                    className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm">
                    {statutsPour(ouvert._type).map((s) => <option key={s.valeur} value={s.valeur}>{s.libelle}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="note">Note interne</Label>
                  <Textarea id="note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Notes privées (non visibles par le client)…" />
                </div>
                <div className="flex flex-wrap justify-end gap-3">
                  <Button type="button" variant="outline" className="gap-2"
                    onClick={() => { onRepondre({ to: ouvert.email, subject: ouvert._type === 'devis' ? 'Votre demande de devis' : 'Votre message' }); setOuvert(null); }}>
                    <Send className="w-4 h-4" aria-hidden="true" /> Écrire un email
                  </Button>
                  <Button onClick={enregistrer} disabled={enregistrement} className="gap-2">
                    {enregistrement && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ------------------------------------------------------------ Onglet envoyés
const OngletEnvoyes = ({ boites, onRepondre, rafraichir }) => {
  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState(null);
  const multi = boites.length > 1;

  const charger = useCallback(async () => {
    setChargement(true);
    try { setItems((await adminListSent()).items || []); }
    catch { toast.error('Chargement des envoyés impossible.'); }
    finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger, rafraichir]);

  if (chargement) return <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!items.length) return <p className="text-center text-gray-500 py-12">Aucun email envoyé depuis le panel.</p>;

  return (
    <>
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
        {items.map((m) => (
          <button key={m.id} onClick={() => setOuvert(m)}
            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3">
            {multi && <Badge className="shrink-0 bg-gray-100 text-gray-600 hover:bg-gray-100 font-normal">{m.from_label}</Badge>}
            <span className="w-48 shrink-0 truncate text-sm text-gray-700">À : {m.to}</span>
            <span className="flex-1 truncate text-sm text-gray-600">{m.subject}</span>
            <span className="text-xs text-gray-400 shrink-0 hidden sm:block">{formatDate(m.created_at)}</span>
          </button>
        ))}
      </div>

      <Dialog open={!!ouvert} onOpenChange={(o) => !o && setOuvert(null)}>
        <DialogContent className="max-w-2xl">
          {ouvert && (
            <>
              <DialogHeader><DialogTitle className="pr-6">{ouvert.subject}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="text-sm text-gray-600 border-b border-gray-100 pb-3">
                  <div><span className="text-gray-400">De :</span> {ouvert.from_label}</div>
                  <div><span className="text-gray-400">À :</span> {ouvert.to}</div>
                  {ouvert.sent_by_name && <div><span className="text-gray-400">Envoyé par :</span> {ouvert.sent_by_name}</div>}
                  <div><span className="text-gray-400">Le :</span> {formatDate(ouvert.created_at)}</div>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 max-h-[50vh] overflow-auto">{ouvert.message}</pre>
                <div className="flex justify-end">
                  <Button className="gap-2"
                    onClick={() => { onRepondre({ to: ouvert.to, subject: `Re: ${ouvert.subject}`, from_mailbox: ouvert.from_mailbox }); setOuvert(null); }}>
                    <Send className="w-4 h-4" aria-hidden="true" /> Réécrire
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// --------------------------------------------------------------- Page
export const AdminCourriel = () => {
  const [onglet, setOnglet] = useState('boite');
  const [boites, setBoites] = useState([]);
  const [composer, setComposer] = useState({ ouvert: false, initial: null });
  const [rafraichirDrafts, setRafraichirDrafts] = useState(0);

  const chargerBoites = useCallback(async () => {
    try {
      const data = await adminMailboxBoites();
      setBoites(data.boites || []);
    } catch { setBoites([]); }
  }, []);

  useEffect(() => { chargerBoites(); }, [chargerBoites]);

  const repondre = (initial) => setComposer({ ouvert: true, initial });
  const ouvrirBrouillon = (d) => setComposer({ ouvert: true, initial: { ...d, draftId: d.id } });

  const onglets = [
    { id: 'boite', libelle: 'Boîte de réception', Icone: Inbox },
    { id: 'envoyes', libelle: 'Envoyés', Icone: Send },
    { id: 'brouillons', libelle: 'Brouillons', Icone: FilePenLine },
    { id: 'demandes', libelle: 'Demandes du site', Icone: Mail },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Courriel</h1>
          <p className="text-gray-600 mt-1">Vos boîtes email et les demandes reçues via le site, en un seul endroit.</p>
        </div>
        <Button className="gap-2" onClick={() => setComposer({ ouvert: true, initial: null })}>
          <PenSquare className="w-4 h-4" aria-hidden="true" /> Nouvel email
        </Button>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {onglets.map(({ id, libelle, Icone }) => (
          <button key={id} onClick={() => setOnglet(id)}
            className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${onglet === id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            <Icone className="w-4 h-4" aria-hidden="true" /> {libelle}
          </button>
        ))}
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 sm:p-6">
          {onglet === 'boite' && <OngletBoite boites={boites} onRepondre={repondre} />}
          {onglet === 'envoyes' && <OngletEnvoyes boites={boites} onRepondre={repondre} rafraichir={rafraichirDrafts} />}
          {onglet === 'brouillons' && <OngletBrouillons onOuvrir={ouvrirBrouillon} rafraichir={rafraichirDrafts} />}
          {onglet === 'demandes' && <OngletDemandes onRepondre={repondre} />}
        </CardContent>
      </Card>

      <Composer
        ouvert={composer.ouvert}
        initial={composer.initial}
        boites={boites}
        onClose={() => setComposer({ ouvert: false, initial: null })}
        onEnvoye={() => { setRafraichirDrafts((n) => n + 1); chargerBoites(); }}
      />
    </div>
  );
};

export default AdminCourriel;
